import { View } from "react-native";
import { Text } from "@shared/components/text";
import { ActivityName } from "app/_modules/core/types";
import { dynamicTranslations } from "@shared/utils/dynamicTranslation";
import { Head } from "@shared/components/head";
import { IStepComponentProps } from "@shared/components/form/types";
import { TAlertType } from "app/_modules/core/constants/alertType";
import { alertTypeResult, IMeasurementData, extractMeasurementValues, getVitalParameterName, formatMeasurementValue, checkMeasurementAlert } from "app/_modules/core/utils";
import { TMeasurementResponse, IPathwayActivity } from "app/_modules/pathways/entities";
import { useAuth } from "app/auth/hooks/useAuth";
import { TThresholdType } from "app/types/alert";

export interface IMeasurementResultStepContext {
    activityName: ActivityName;
    alertType?: TAlertType;
    result?: TMeasurementResponse;
    isRemeasurement?: boolean;
    hasRemainingActivities?: boolean
    nextActivity?: IPathwayActivity
}

export const MeasurementResultStep = ({ context }: IStepComponentProps<undefined, IMeasurementResultStepContext>) => {
    const { meData } = useAuth()
    const { t } = dynamicTranslations();

    const activityName = context?.activityName!
    const result = context?.result;
    const isRemeasurement = context?.isRemeasurement || false;

    let measurementValues: Record<string, any> = {};
    let finalAlertType: TAlertType = 'green';
    let thresholdType: TThresholdType | undefined;

    // Fallback: backend does not always include alert in the payload (e.g. historical REST data).
    // If alert is null the backend explicitly reported no alert — skip client-side recomputation.
    if (result?.alert?.alertDetails && result.alert.alertDetails.length > 0) {
        finalAlertType = result.alert.alertDetails[0].alertType;
        thresholdType = result.alert.alertDetails[0].thresholdType;
    } else if (result?.measurement && result.alert === undefined) {
        const vitalParameterData = meData?.current_pathway?.vital_parameters?.find(
            vp => vp.vital_parameter.name === activityName
        );
        if (vitalParameterData?.vital_parameter?.monitoring_parameters?.length) {
            const checked = checkMeasurementAlert(
                result.measurement as Record<string, number>,
                vitalParameterData.vital_parameter.monitoring_parameters
            );
            finalAlertType = checked.alertType;
            thresholdType = checked.thresholdType;
        }
    }

    if (result?.measurement) {
        const measurementType = result.type || activityName;
        const measurementData: IMeasurementData = {
            type: measurementType,
            measurement: result.measurement,
            timestamp: new Date().toISOString(),
            telemonitoringPathwayId: meData?.current_pathway?.id
        };

        const extracted = extractMeasurementValues(measurementData);
        measurementValues = extracted;
    }

    const { color, title, description, type } = alertTypeResult(activityName, finalAlertType, thresholdType);

    const { value: primaryValue, unit: displayUnit, showUnit } = formatMeasurementValue(activityName, measurementValues, t);

    const isRemeasurementWithAbnormalValues = isRemeasurement && (finalAlertType === 'red' || finalAlertType === 'yellow');

    let valueFontSize = '!text-[64px]';
    if (activityName === ActivityName.BLOOD_PRESSURE) {
        valueFontSize = '!text-[28px]';
    } else if (activityName === ActivityName.ECG || activityName === ActivityName.WEIGHT || activityName === ActivityName.TEMPERATURE) {
        valueFontSize = '!text-[32px]';
    }

    return (
        <View className="flex-1 items-center justify-center gap-4">
            <View className={`items-center justify-center h-[140px] w-[140px] rounded-full border-4 ${color.bg}`}>
                <View className="items-center justify-center">
                    <Text className={`${valueFontSize} font-bold leading-none ${color.text}`}>{primaryValue}</Text>
                    {showUnit && <Text className={`leading-none text-base -mt-2 ${color.text}`}>{displayUnit}</Text>}
                </View>
            </View>
            <View className="flex-col gap-4">
                {isRemeasurementWithAbnormalValues ? (
                    <>
                        <Head className="text-center" type="h2">
                            {t(getVitalParameterName(activityName))} {t('screens.remeasurement.result.still_abnormal.title')}
                        </Head>
                        <View className="mx-4">
                            <Text className="text-center  font-medium">
                                {t('screens.remeasurement.result.still_abnormal.description')}
                            </Text>
                        </View>
                    </>
                ) : (
                    <>
                        <Head className="text-center" type="h2">{type ? `${t(type)} ` : ''}{t(title)}</Head>
                        <Text className="text-center">{t(description)}</Text>
                    </>
                )}
            </View>
        </View>
    )
}
